import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import ConfirmModal from '../../components/ConfirmModal';
import API, { resolvePhoto } from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, ChevronUp, ChevronDown, Image, Type } from 'lucide-react';

const PRESETS = [
  { label: 'Oranye', bg: '#e85d04', text: '#ffffff' },
  { label: 'Biru',   bg: '#0D1B2A', text: '#ffffff' },
  { label: 'Merah',  bg: '#c1121f', text: '#ffffff' },
  { label: 'Hijau',  bg: '#2d6a4f', text: '#ffffff' },
  { label: 'Kuning', bg: '#f4d03f', text: '#1a1a1a' },
  { label: 'Ungu',   bg: '#6d28d9', text: '#ffffff' },
];

const EMPTY_SLIDE = {
  isActive: true, type: 'text', text: '', linkUrl: '', linkLabel: '',
  bgColor: '#e85d04', textColor: '#ffffff', imageUrl: '', imageAlt: '',
};

function SlidePreview({ b }) {
  if (b.type === 'image' && b.imageUrl) {
    return (
      <img
        src={resolvePhoto(b.imageUrl)} alt={b.imageAlt || 'Banner'}
        style={{ width: '100%', maxHeight: 80, objectFit: 'contain', display: 'block', background: '#f4f4f4' }}
      />
    );
  }
  return (
    <div style={{
      background: b.bgColor || '#e85d04', color: b.textColor || '#fff',
      padding: '10px 20px', fontSize: 13, fontWeight: 600, textAlign: 'center',
      minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    }}>
      <span>{b.text || <span style={{ opacity: 0.4 }}>Teks banner...</span>}</span>
      {b.linkLabel && <span style={{ fontWeight: 800, textDecoration: 'underline' }}>{b.linkLabel} →</span>}
    </div>
  );
}

function SlideEditor({ slide, onChange, onUploadImage, onRemoveImage, uploading }) {
  const imgRef = useRef(null);
  const set = (k, v) => onChange({ ...slide, [k]: v });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Tipe */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--ki-text-muted)' }}>Tipe Slide</div>
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ki-border)' }}>
          {[
            { val: 'text',  icon: <Type size={14} />,  label: 'Teks' },
            { val: 'image', icon: <Image size={14} />, label: 'Gambar' },
          ].map(t => (
            <button key={t.val} onClick={() => set('type', t.val)} style={{
              flex: 1, padding: '8px 0', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
              background: slide.type === t.val ? 'var(--ki-purple)' : 'var(--ki-card)',
              color: slide.type === t.val ? '#fff' : 'var(--ki-text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.15s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mode teks */}
      {slide.type === 'text' && (
        <>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Teks Banner</label>
            <textarea value={slide.text} onChange={e => set('text', e.target.value)}
              rows={2} maxLength={200} placeholder="Teks yang ditampilkan..."
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', background: 'var(--ki-bg)', color: 'var(--ki-text)', boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11, color: 'var(--ki-text-faint)', marginTop: 2 }}>{(slide.text || '').length}/200</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>URL Tautan</label>
              <input type="url" value={slide.linkUrl} onChange={e => set('linkUrl', e.target.value)} placeholder="https://..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 13, background: 'var(--ki-bg)', color: 'var(--ki-text)', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Label Tautan</label>
              <input type="text" value={slide.linkLabel} onChange={e => set('linkLabel', e.target.value)} placeholder="Daftar Sekarang"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 13, background: 'var(--ki-bg)', color: 'var(--ki-text)', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 8 }}>Warna</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {PRESETS.map(p => (
                <button key={p.label} onClick={() => { set('bgColor', p.bg); onChange({ ...slide, bgColor: p.bg, textColor: p.text }); }}
                  title={p.label}
                  style={{ width: 28, height: 28, borderRadius: 6, background: p.bg, border: slide.bgColor === p.bg ? '3px solid var(--ki-text)' : '2px solid transparent', cursor: 'pointer' }} />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[['bgColor', 'Latar'], ['textColor', 'Teks']].map(([field, label]) => (
                <div key={field} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="color" value={slide[field] || '#000000'} onChange={e => set(field, e.target.value)}
                    style={{ width: 34, height: 32, borderRadius: 6, border: '1px solid var(--ki-border)', cursor: 'pointer', padding: 2 }} />
                  <input type="text" value={slide[field]} onChange={e => set(field, e.target.value)} placeholder={label}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 12, background: 'var(--ki-bg)', color: 'var(--ki-text)' }} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Mode gambar */}
      {slide.type === 'image' && (
        <>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 8 }}>Gambar Banner</label>
            <div style={{ fontSize: 11, color: 'var(--ki-text-faint)', marginBottom: 8 }}>
              Rekomendasi: 1200 x 120 px, rasio lebar, maks. 2 MB (JPG/PNG/WebP)
            </div>
            {slide.imageUrl ? (
              <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ki-border)', marginBottom: 8 }}>
                <img src={resolvePhoto(slide.imageUrl)} alt="" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', display: 'block', background: '#f4f4f4' }} />
              </div>
            ) : (
              <div style={{ border: '2px dashed var(--ki-border)', borderRadius: 8, padding: '20px', textAlign: 'center', marginBottom: 8, background: 'var(--ki-bg)' }}>
                <div style={{ fontSize: 12, color: 'var(--ki-text-faint)' }}>Belum ada gambar</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{
                padding: '7px 14px', background: 'var(--in-blue)', color: '#fff', borderRadius: 8,
                fontWeight: 700, fontSize: 12, cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <Image size={13} /> {uploading ? 'Mengunggah...' : slide.imageUrl ? 'Ganti Gambar' : 'Unggah Gambar'}
                <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                  onChange={e => onUploadImage(e.target.files?.[0])} disabled={uploading} />
              </label>
              {slide.imageUrl && (
                <button onClick={onRemoveImage} className="btn btn-secondary btn-sm">
                  Hapus Gambar
                </button>
              )}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Teks Alternatif (alt)</label>
            <input type="text" value={slide.imageAlt} onChange={e => set('imageAlt', e.target.value)}
              placeholder="Deskripsi gambar untuk aksesibilitas"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 13, background: 'var(--ki-bg)', color: 'var(--ki-text)', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>URL Tautan</label>
              <input type="url" value={slide.linkUrl} onChange={e => set('linkUrl', e.target.value)} placeholder="https://..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 13, background: 'var(--ki-bg)', color: 'var(--ki-text)', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>Label Tautan</label>
              <input type="text" value={slide.linkLabel} onChange={e => set('linkLabel', e.target.value)} placeholder="Kunjungi"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ki-border)', fontSize: 13, background: 'var(--ki-bg)', color: 'var(--ki-text)', boxSizing: 'border-box' }} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminBanner() {
  const [slides, setSlides] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    try {
      const r = await API.get('/banner/all');
      setSlides(r.data.banners || []);
    } catch { toast.error('Gagal memuat banner'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const selectedSlide = slides.find(s => s._id === selected);

  const handleChange = (updated) => {
    setSlides(prev => prev.map(s => s._id === selected ? { ...s, ...updated } : s));
  };

  const saveSlide = async () => {
    if (!selectedSlide) return;
    setSaving(true);
    try {
      await API.patch(`/banner/${selected}`, selectedSlide);
      toast.success('Slide disimpan');
    } catch { toast.error('Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (slide) => {
    try {
      const r = await API.patch(`/banner/${slide._id}`, { isActive: !slide.isActive });
      setSlides(prev => prev.map(s => s._id === slide._id ? r.data.banner : s));
    } catch { toast.error('Gagal mengubah status'); }
  };

  const deleteSlide = async (id) => {
    try {
      await API.delete(`/banner/${id}`);
      setSlides(prev => prev.filter(s => s._id !== id));
      if (selected === id) setSelected(null);
      toast.success('Slide dihapus');
    } catch { toast.error('Gagal menghapus'); }
    finally { setDeleteConfirm(null); }
  };

  const addSlide = async () => {
    setAdding(true);
    try {
      const r = await API.post('/banner', { ...EMPTY_SLIDE, order: slides.length });
      setSlides(prev => [...prev, r.data.banner]);
      setSelected(r.data.banner._id);
      toast.success('Slide baru ditambahkan');
    } catch { toast.error('Gagal menambah slide'); }
    finally { setAdding(false); }
  };

  const moveSlide = async (id, dir) => {
    const idx = slides.findIndex(s => s._id === id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= slides.length) return;
    const next = [...slides];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setSlides(next);
    try {
      await Promise.all([
        API.patch(`/banner/${next[idx]._id}`,   { order: idx }),
        API.patch(`/banner/${next[swapIdx]._id}`, { order: swapIdx }),
      ]);
    } catch { toast.error('Gagal mengubah urutan'); load(); }
  };

  const handleUploadImage = async (file) => {
    if (!file || !selected) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Maks 2 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await API.post(`/banner/${selected}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSlides(prev => prev.map(s => s._id === selected ? r.data.banner : s));
      toast.success('Gambar diunggah');
    } catch { toast.error('Gagal mengunggah gambar'); }
    finally { setUploading(false); }
  };

  const handleRemoveImage = async () => {
    if (!selected) return;
    try {
      const r = await API.delete(`/banner/${selected}/image`);
      setSlides(prev => prev.map(s => s._id === selected ? r.data.banner : s));
      toast.success('Gambar dihapus');
    } catch { toast.error('Gagal menghapus gambar'); }
  };

  if (loading) return <DashboardLayout><div style={{ padding: 40, color: 'var(--ki-text-muted)' }}>Memuat...</div></DashboardLayout>;

  return (
    <DashboardLayout title="Banner dan Iklan" subtitle="Kelola carousel banner di halaman utama">
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, maxWidth: 960 }}>

        {/* Panel kiri: daftar slide */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--ki-text-faint)' }}>
              Slide ({slides.length})
            </div>
            <button onClick={addSlide} disabled={adding} className="btn btn-primary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Plus size={13} /> Tambah
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {slides.length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--ki-text-faint)', fontSize: 13, border: '2px dashed var(--ki-border)', borderRadius: 10 }}>
                Belum ada slide. Klik Tambah untuk membuat.
              </div>
            )}
            {slides.map((s, i) => (
              <div
                key={s._id}
                onClick={() => setSelected(s._id)}
                style={{
                  border: `2px solid ${selected === s._id ? 'var(--ki-purple)' : 'var(--ki-border)'}`,
                  borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                  background: 'var(--ki-card)', opacity: s.isActive ? 1 : 0.55,
                  transition: 'border-color 0.15s',
                }}
              >
                {/* Preview mini */}
                <div style={{ pointerEvents: 'none' }}>
                  <SlidePreview b={s} />
                </div>

                {/* Kontrol */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderTop: '1px solid var(--ki-border)' }}
                  onClick={e => e.stopPropagation()}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: s.isActive ? '#16a34a' : 'var(--ki-text-faint)', flex: 1 }}>
                    {s.isActive ? 'Aktif' : 'Nonaktif'}
                  </span>
                  <button title="Naik" onClick={() => moveSlide(s._id, -1)} disabled={i === 0}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ki-text-faint)', padding: '2px 4px', opacity: i === 0 ? 0.3 : 1 }}>
                    <ChevronUp size={14} />
                  </button>
                  <button title="Turun" onClick={() => moveSlide(s._id, 1)} disabled={i === slides.length - 1}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ki-text-faint)', padding: '2px 4px', opacity: i === slides.length - 1 ? 0.3 : 1 }}>
                    <ChevronDown size={14} />
                  </button>
                  <button title={s.isActive ? 'Nonaktifkan' : 'Aktifkan'} onClick={() => toggleActive(s)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                      color: 'var(--ki-text-muted)', padding: '2px 6px' }}>
                    {s.isActive ? 'Off' : 'On'}
                  </button>
                  <button title="Hapus" onClick={() => setDeleteConfirm(s._id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ki-text-muted)', padding: '2px 4px' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Panel kanan: editor slide yang dipilih */}
        <div>
          {!selectedSlide ? (
            <div style={{ padding: '60px 24px', textAlign: 'center', color: 'var(--ki-text-faint)', border: '2px dashed var(--ki-border)', borderRadius: 10, fontSize: 14 }}>
              Pilih slide di sebelah kiri untuk mengedit, atau tambah slide baru.
            </div>
          ) : (
            <div style={{ background: 'var(--ki-card)', border: '1px solid var(--ki-border)', borderRadius: 10, padding: '20px 24px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 20 }}>
                Edit Slide {slides.findIndex(s => s._id === selected) + 1}
              </div>

              <SlideEditor
                slide={selectedSlide}
                onChange={handleChange}
                onUploadImage={handleUploadImage}
                onRemoveImage={handleRemoveImage}
                uploading={uploading}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button onClick={saveSlide} disabled={saving} className="btn btn-primary"
                  style={{ opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Menyimpan...' : 'Simpan Slide'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        open={!!deleteConfirm}
        title="Hapus Slide"
        message="Slide ini akan dihapus permanen dan tidak dapat dikembalikan."
        confirmLabel="Hapus"
        onConfirm={() => deleteSlide(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </DashboardLayout>
  );
}
